import React, { useState, useEffect } from "react";
import { createGlobalStyle } from "styled-components";
import { useSnackbar } from "notistack";
import { FONTS, T } from "./tokens/index.js";
import {
  ModalShell,
  ModalHeader,
  StepProgressStrip,
  ModalBody,
  ModalFooter,
} from "../HearingBundles/components/Modalshell.js";
import { Toast } from "../HearingBundles/components/Primitives.js";
import { Icon } from "../HearingBundles/components/Icons.js";

import Step1NamePurpose, {
  buildBundleTabsFromConfigs,
} from "../HearingBundles/steps/Step1namepurpose.js";
import Step2PickPages from "../HearingBundles/steps/Step2pickpages.js";
import Step3OrganiseBundles from "../HearingBundles/steps/Step3organisebundles.js";
import Step4Review from "../HearingBundles/steps/Step4Review.js";
import useDocuments from "../../../../hooks/useDocuments.js";
import { PrimaryCTAButton } from "../../Buttons.js";
import BundleService from "../../../../services/BundleService.js";
import useLoader from "../../../../hooks/useLoader.js";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;0,800;1,300;1,400;1,600;1,700;1,800&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
`;

const STEP_LABELS = [
  "Name & Purpose",
  "Pick Pages",
  "Organise Bundles",
  "Review & Create",
];

const DEFAULT_BUNDLE_CONFIGS = [{ subFolderName: "", prefix: "" }];

export const getPageCount = (pages = "") => {
  return pages.split(",").reduce((count, part) => {
    part = part.trim();
    if (part.includes("-")) {
      const [start, end] = part.split("-").map(Number);
      return count + (end - start + 1);
    }
    return part ? count + 1 : count;
  }, 0);
};

// Converts a Set of page numbers into a compact range string e.g. "1-5,8,11-13"
const pagesToRangeString = (pageSet) => {
  if (!pageSet || pageSet.size === 0) return "";
  const pages = Array.from(pageSet).sort((a, b) => a - b);
  if (pages.length === 1) return String(pages[0]);

  const ranges = [];
  let start = pages[0];
  let prev = pages[0];

  for (let i = 1; i < pages.length; i++) {
    if (pages[i] !== prev + 1) {
      ranges.push(start === prev ? String(start) : `${start}-${prev}`);
      start = pages[i];
    }
    prev = pages[i];
  }
  ranges.push(start === prev ? String(start) : `${start}-${prev}`);

  return ranges.join(",");
};

/**
 * buildAutoAssignments
 *
 * checked = false  → all docs go into the single flat bundle (bundles[0])
 * checked = true   → docs are distributed round-robin across sub-folder bundles
 */
const buildAutoAssignments = (
  pickedDocIds,
  pickedPages,
  documents,
  bundles,
  checked,
) => {
  const byBundle = {};
  bundles.forEach((b) => (byBundle[b.code] = []));

  if (!bundles.length) return byBundle;

  pickedDocIds.forEach((docId, docIndex) => {
    const doc = documents.find((d) => String(d.id) === String(docId));
    if (!doc) return;

    const pageSet = pickedPages[docId];
    if (!pageSet || pageSet.size === 0) return;

    // No-subfolder: always target the single bundle
    // Subfolder: round-robin across all bundles
    const targetBundle = !checked
      ? bundles[0]
      : bundles[docIndex % bundles.length];

    const targetCode = targetBundle.code;
    const tgt = byBundle[targetCode];

    const usedTabs = new Set(tgt.map((a) => a.tabNum));
    let tabNum = 1;
    while (usedTabs.has(tabNum)) tabNum++;

    // Use compact range string, not raw comma-separated page numbers
    const pagesStr = pagesToRangeString(pageSet);

    tgt.push({
      sourceDocId: docId,
      id: doc.id,
      fileUrl: doc.fileUrl,
      tabNum,
      userLabel: doc.name,
      pages: pagesStr,
      visibilityType: "me",
    });
  });

  return byBundle;
};

const deriveBundlesFromConfigs = (bundleConfigs) => {
  const tabs = buildBundleTabsFromConfigs(bundleConfigs);
  return tabs.map((t) => ({ ...t, assignments: [] }));
};

const CreateTrialBundleModal = ({
  open,
  onClose,
  onCreated,
  caseId,
  CustomInputField,
  CustomSelect,
  trigger,
  setTrigger,
  fetchAllBundles = () => {},
  coreBundle = false,
}) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  const [step, setStep] = useState(1);

  // Step 1 state
  const [name, setName] = useState("");
  const [bundleType, setBundleType] = useState("Trial Bundle");
  const [hearing, setHearing] = useState("");
  const [bundleCount, setBundleCount] = useState("1");
  const [bundleConfigs, setBundleConfigs] = useState(DEFAULT_BUNDLE_CONFIGS);
  const [checked, setChecked] = useState(true);
  const [addFooterPageNumbers, setAddFooterPageNumbers] = useState(true);

  // Step 2 state
  const [pickedPages, setPickedPages] = useState({});
  const [sourceFilter, setSourceFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [expandedDocs, setExpandedDocs] = useState({});
  const [error, setError] = useState("");

  const [bundles, setBundles] = useState(() =>
    deriveBundlesFromConfigs(DEFAULT_BUNDLE_CONFIGS),
  );

  const {
    documents,
    docsWithPages,
    loading: loadingDocs,
    error: docsError,
  } = useDocuments(open ? caseId : null, sourceFilter);

  useEffect(() => {
    if (docsError) enqueueSnackbar(docsError, { variant: "error" });
  }, [docsError, enqueueSnackbar]);

  useEffect(() => {
    if (open) {
      setStep(1);
      setName("");
      setBundleType("Trial Bundle");
      setHearing("");
      setBundleCount("1");
      setBundleConfigs(DEFAULT_BUNDLE_CONFIGS);
      setPickedPages({});
      setSourceFilter("All");
      setSearch("");
      setExpandedDocs({});
      setError("");
      setChecked(true);
      setAddFooterPageNumbers(true);
    }
  }, [open]);

  useEffect(() => {
    if (!checked) {
      const flatConfig = [
        { subFolderName: name || "Main Bundle", prefix: "TB" },
      ];
      setBundleConfigs(flatConfig);
    }
  }, [checked, name]);

  useEffect(() => {
    const newTabs = buildBundleTabsFromConfigs(bundleConfigs);
    setBundles((prev) =>
      newTabs.map((t) => {
        const existing = prev.find((b) => b.code === t.code);
        return {
          ...t,
          assignments: existing?.assignments ?? [],
        };
      }),
    );
  }, [bundleConfigs]);

  const pickedDocIds = Object.keys(pickedPages).filter(
    (id) => pickedPages[id]?.size > 0,
  );

  const totalPickedPages = Object.values(pickedPages).reduce(
    (s, set) => s + (set?.size || 0),
    0,
  );

  const totalAssigned = bundles.reduce(
    (total, bundle) =>
      total +
      (bundle.assignments || []).reduce(
        (sum, assignment) => sum + getPageCount(assignment.pages),
        0,
      ),
    0,
  );

  const totalTabs = bundles.reduce(
    (sum, b) => sum + new Set(b.assignments.map((a) => a.tabNum)).size,
    0,
  );

  const assignedDocIds = new Set(
    bundles.flatMap((b) => b.assignments.map((a) => String(a.sourceDocId))),
  );

  /*

  const allDocsAssigned = pickedDocIds.every((id) =>
    assignedDocIds.has(String(id)),
  );

  */

  const canNext = () => {
    if (step === 1) {
      if (!name.trim()) return false;
      if (checked) {
        return bundleConfigs.every(
          (cfg) => cfg.subFolderName?.trim() && cfg.prefix?.trim(),
        );
      }
      return true;
    }
    if (step === 2) {
      return totalPickedPages > 0 && totalPickedPages <= 2000;
    }
    if (step === 3) {
      return true;
    }
    return true;
  };

  const handleAutoAssign = () => {
    const byBundle = buildAutoAssignments(
      pickedDocIds,
      pickedPages,
      documents,
      bundles,
      checked,
    );
    setBundles((prev) =>
      prev.map((b) => ({ ...b, assignments: byBundle[b.code] || [] })),
    );
  };

  const buildFinalPayload = (
    bundles,
    checked,
    name,
    bundleType,
    hearing,
    caseId,
    totalAssigned,
    bundleConfigs,
    pickedDocIds,
    pickedPages,
    documents,
    addFooterPageNumbers = true,
  ) => {
    const assignedDocIds = new Set(
      bundles.flatMap((b) => b.assignments.map((a) => String(a.sourceDocId))),
    );

    const unassignedEntries = pickedDocIds
      .filter((id) => !assignedDocIds.has(String(id)))
      .map((docId) => {
        const doc = documents.find((d) => String(d.id) === String(docId));
        const pageSet = pickedPages[docId];
        const pagesStr = pagesToRangeString(pageSet);
        return {
          sourceDocId: docId,
          id: doc?.id ?? docId,
          fileUrl: doc?.fileUrl ?? "",
          userLabel: doc ? doc.name : `Document ${docId}`,
          pages: pagesStr || "1",
          visibilityType: "me",
        };
      });

    const unassignedPageCount = unassignedEntries.reduce(
      (sum, a) => sum + getPageCount(a.pages),
      0,
    );
    const grandTotalPages = totalAssigned + unassignedPageCount;

    if (!checked) {
      const allAssignments = [
        ...bundles[0].assignments.map(({ tabNum, ...a }) => a),
        ...unassignedEntries,
      ];
      return {
        bundleCode: "B" + Math.floor(100 + Math.random() * 900),
        name,
        bundleType,
        hearing,
        caseId,
        totalPages: grandTotalPages,
        visibilityType: "me",
        bundleTabs: [{ assignments: allAssignments }],
        add_footer_page_numbers: Boolean(addFooterPageNumbers),
      };
    }

    const populatedBundles = bundles.filter((b) => b.assignments.length > 0);

    const bundleTabsPayload = populatedBundles.map((b) => ({
      code: b.code,
      name: b.name,
      prefix: b.prefix,
      assignments: b.assignments.map(({ tabNum, ...a }) => a),
    }));

    if (unassignedEntries.length > 0) {
      bundleTabsPayload.push({ assignments: unassignedEntries });
    }

    const filteredBundleFolders = bundleConfigs.filter(
      (cfg) => cfg.subFolderName?.trim() && cfg.prefix?.trim(),
    );

    return {
      bundleCode: "B" + Math.floor(100 + Math.random() * 900),
      name,
      bundleType,
      hearing,
      caseId,
      totalPages: grandTotalPages,
      ...(filteredBundleFolders.length > 0
        ? { bundleFolders: filteredBundleFolders }
        : {}),
      visibilityType: "me",
      bundleTabs: bundleTabsPayload,
      add_footer_page_numbers: Boolean(addFooterPageNumbers),
    };
  };

  const handleCreate = async () => {
    const finalPayload = buildFinalPayload(
      bundles,
      checked,
      name,
      bundleType,
      hearing,
      caseId,
      totalAssigned,
      bundleConfigs,
      pickedDocIds,
      pickedPages,
      documents,
      addFooterPageNumbers,
    );
    try {
      setLoader({ state: true, message: "create bundle..." });
      const response = await BundleService.createBundle(finalPayload);
      const createdBundle = response?.data ?? response;
      if (response) {
        enqueueSnackbar("Bundle created successfully", { variant: "success" });
        if (typeof fetchAllBundles === "function") await fetchAllBundles();
        if (typeof setTrigger === "function") setTrigger(true);
        if (typeof onCreated === "function") onCreated(createdBundle);
        onClose();
      }
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  };

  // Add after existing derived values
  const unassignedDocEntries = pickedDocIds
    .filter((id) => !assignedDocIds.has(String(id)))
    .map((docId) => {
      const doc = documents.find((d) => String(d.id) === String(docId));
      const pageSet = pickedPages[docId];
      const pagesStr = pagesToRangeString(pageSet);
      return {
        sourceDocId: docId,
        id: doc?.id ?? docId,
        fileUrl: doc?.fileUrl ?? "",
        userLabel: doc ? doc.name : `Document ${docId}`,
        pages: pagesStr || "1",
        visibilityType: "me",
      };
    });

  return (
    <>
      <GlobalStyle />
      <ModalShell open={open} onClose={onClose}>
        <ModalHeader step={step} stepLabels={STEP_LABELS} onClose={onClose} />
        <StepProgressStrip
          step={step}
          stepLabels={STEP_LABELS}
          onJump={setStep}
        />

        <ModalBody>
          {step === 1 && (
            <Step1NamePurpose
              name={name}
              setName={setName}
              bundleType={bundleType}
              setBundleType={setBundleType}
              hearing={hearing}
              setHearing={setHearing}
              bundleCount={bundleCount}
              setBundleCount={setBundleCount}
              bundleConfigs={bundleConfigs}
              setBundleConfigs={setBundleConfigs}
              CustomInputField={CustomInputField}
              CustomSelect={CustomSelect}
              error={error}
              setError={setError}
              checked={checked}
              setChecked={setChecked}
              addFooterPageNumbers={addFooterPageNumbers}
              setAddFooterPageNumbers={setAddFooterPageNumbers}
            />
          )}

          {step === 2 && (
            <Step2PickPages
              documents={docsWithPages}
              loadingDocs={loadingDocs}
              pickedPages={pickedPages}
              setPickedPages={setPickedPages}
              sourceFilter={sourceFilter}
              setSourceFilter={setSourceFilter}
              search={search}
              setSearch={setSearch}
              expandedDocs={expandedDocs}
              setExpandedDocs={setExpandedDocs}
            />
          )}

          {step === 3 && (
            <Step3OrganiseBundles
              documents={documents}
              pickedPages={pickedPages}
              bundles={bundles}
              setBundles={setBundles}
              onAutoAssign={handleAutoAssign}
              checked={checked}
            />
          )}

          {step === 4 && (
            <Step4Review
              name={name}
              bundleType={bundleType}
              bundleConfigs={bundleConfigs}
              hearing={hearing}
              bundles={bundles}
              pickedDocIds={pickedDocIds}
              totalAssigned={totalAssigned}
              totalTabs={totalTabs}
              checked={checked}
              unassignedDocEntries={unassignedDocEntries}
            />
          )}
        </ModalBody>

        <ModalFooter
          step={step}
          totalSteps={STEP_LABELS.length}
          canNext={canNext()}
          onBack={() => setStep((s) => s - 1)}
          onNext={() => setStep((s) => s + 1)}
          onClose={onClose}
          onCreate={handleCreate}
        />
      </ModalShell>
    </>
  );
};

export default CreateTrialBundleModal;

export const CreateTrialBundleModalDemo = () => {
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState(null);

  const handleCreated = (bundle) => {
    setToast(`Bundle "${bundle.name}" created — ${bundle.totalPages} pages.`);
    setTimeout(() => setToast(null), 3500);
  };

  return (
    <div
      style={{
        fontFamily: FONTS.primaryFontRegular,
        padding: 40,
        minHeight: "100vh",
        background: T.soft,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 16,
      }}
    >
      <PrimaryCTAButton
        onClick={() => setOpen(true)}
        style={{ display: "inline-flex", alignItems: "center", width: "auto" }}
      >
        Create Trial Bundle
      </PrimaryCTAButton>

      <CreateTrialBundleModal
        open={open}
        onClose={() => setOpen(false)}
        onCreated={handleCreated}
        caseId="demo-case-123"
      />

      {toast && (
        <Toast>
          <Icon.Check size={14} color={T.highlight} />
          {toast}
        </Toast>
      )}
    </div>
  );
};
